import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * DateField — themed wrapper around shadcn Calendar (popover-triggered)
 * with the same external contract as `<input type="date">`:
 *   value = "" | "yyyy-mm-dd"
 *   onChange("yyyy-mm-dd" | "")
 *
 * Use this instead of native `<input type="date">` anywhere consistency with
 * the admin theme matters (no browser-native picker chrome).
 */
export default function DateField({ label, value, onChange, testid, placeholder = "Pick a date", minDate }) {
    const [open, setOpen] = useState(false);

    // Parse the stored ISO date (yyyy-mm-dd) into a Date object at LOCAL
    // midnight. Using `new Date("yyyy-mm-dd")` would parse as UTC midnight,
    // which shifts a day in negative timezones — `new Date(y, m-1, d)` keeps it
    // grounded in the user's local day.
    const selected = (() => {
        if (!value) return undefined;
        const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
        if (!y || !m || !d) return undefined;
        return new Date(y, m - 1, d);
    })();

    // Optional minimum-date constraint (parsed the same way as `value`). Used
    // e.g. on "expected_delivery" pickers where the date must be on/after
    // `order_date` — fed straight to react-day-picker's `disabled` matcher.
    const minDateObj = (() => {
        if (!minDate) return undefined;
        const [y, m, d] = minDate.split("-").map((n) => parseInt(n, 10));
        if (!y || !m || !d) return undefined;
        return new Date(y, m - 1, d);
    })();

    const handleSelect = (date) => {
        if (!date) { onChange(""); return; }
        // Emit yyyy-mm-dd in local time (NOT toISOString — that would convert
        // to UTC and again risk an off-by-one day in negative timezones).
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        onChange(`${y}-${m}-${d}`);
        setOpen(false);
    };

    const display = selected
        ? selected.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "";

    return (
        <label className="block">
            {label && (
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">
                    {label}
                </div>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid={testid}
                        className={`w-full px-3.5 py-2.5 rounded-lg border border-slate-200 bg-white text-left text-[14px] outline-none focus:border-cyan-500 inline-flex items-center justify-between gap-2 transition ${
                            !display ? "text-slate-400" : "text-slate-900"
                        }`}
                    >
                        <span className="truncate">{display || placeholder}</span>
                        <CalendarIcon size={15} className="text-slate-400 shrink-0" />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                    <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={handleSelect}
                        disabled={minDateObj ? { before: minDateObj } : undefined}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </label>
    );
}
